import { useState, type Dispatch, type SetStateAction } from 'react';
import { api } from './api';
import { clearSwrCacheByPrefix, writeSwrCache } from './swrCache';
import type { ExtractedEvent } from '../types';
import { toast } from './dialogs';

/** Corrected date/time payload for the "Fix date/time" menu action. */
export interface DateCorrection {
  startUtc: string;
  endUtc?: string | null;
  isAllDay?: boolean;
}

export interface RecalculateDateController {
  /** Set of event rowKeys currently being recalculated (one per concurrent click). */
  recalculatingIds: ReadonlySet<string>;
  /** Set of event rowKeys currently being marked wrong / corrected. */
  markingWrongIds: ReadonlySet<string>;
  /** Trigger the per-event recalculate-date endpoint and refresh `events` when it lands. */
  recalculateDate: (event: ExtractedEvent) => Promise<void>;
  /**
   * Flag an event's date/time as wrong. With `correction` the date is fixed in
   * place; without it the event is just badged "Wrong date" for AI-training
   * review. Either way the change is synchronous, so we refresh once.
   */
  markWrongDate: (event: ExtractedEvent, correction?: DateCorrection) => Promise<void>;
}

interface RecalculationQueued {
  operationId: string;
  operationPartitionKey: string;
}

interface RecalculationStatus {
  completedAt?: string;
  completionError?: string;
}

interface RecalculationStatusResponse {
  items: RecalculationStatus[];
}

/**
 * Admin helper for the per-event calendar menu.
 *
 * `recalculateDate` posts to `POST /api/mgmt/events/{id}/recalculate-date`,
 * which preserves the prior extracted siblings, creates a durable operation,
 * and enqueues a staged ExtractEvent message. The queue worker reconciles the
 * replacement asynchronously, so we poll that exact operation rather than
 * guessing completion from an event row disappearing or changing.
 *
 * `markWrongDate` posts to `POST /api/mgmt/events/{id}/flag-wrong-date`. That
 * endpoint responds synchronously (either badging the row or applying the
 * corrected date), so we just refresh the list once.
 *
 * @param fetchEvents     Re-runs the same `events` query after the exact
 *                        recalculation operation reports completion.
 * @param eventsCacheKey  SWR cache key to refresh on success (so other open
 *                        windows / tabs see the update). When omitted, we
 *                        clear all `events:*` keys instead.
 * @param onEvents        React state setter for the page's local events list.
 *                        Accepts either a replacement array or a functional
 *                        updater, so the mark-wrong path can optimistically
 *                        patch a single row before the reconcile refetch lands.
 */
export function useRecalculateDate(
  fetchEvents: () => Promise<ExtractedEvent[]>,
  onEvents: Dispatch<SetStateAction<ExtractedEvent[] | null>>,
  eventsCacheKey?: string,
): RecalculateDateController {
  const [recalculatingIds, setRecalculatingIds] = useState<ReadonlySet<string>>(() => new Set<string>());
  const [markingWrongIds, setMarkingWrongIds] = useState<ReadonlySet<string>>(() => new Set<string>());

  const recalculateDate = async (event: ExtractedEvent): Promise<void> => {
    if (recalculatingIds.has(event.rowKey)) return;
    setRecalculatingIds((prev) => {
      const next = new Set(prev);
      next.add(event.rowKey);
      return next;
    });
    const release = () => setRecalculatingIds((prev) => {
      if (!prev.has(event.rowKey)) return prev;
      const next = new Set(prev);
      next.delete(event.rowKey);
      return next;
    });
    try {
      const queued = await api.post<RecalculationQueued>(
        `/api/mgmt/events/${encodeURIComponent(event.rowKey)}/recalculate-date`
      );
      const deadline = Date.now() + 90_000;
      let landed = false;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3_000));
        try {
          const query = new URLSearchParams({
            operationId: queued.operationId,
            partitionKey: queued.operationPartitionKey,
            includePending: 'true'
          });
          const status = await api.get<RecalculationStatusResponse>(
            `/api/mgmt/events/recalculations?${query.toString()}`
          );
          const operation = status.items[0];
          if (!operation?.completedAt) continue;

          const fresh = await fetchEvents();
          if (eventsCacheKey) writeSwrCache(eventsCacheKey, fresh);
          onEvents(fresh);
          if (operation.completionError) {
            toast.warning('Recalculation finished, but some details still need review.');
          } else {
            toast.success('Recalculation complete.');
          }
          landed = true;
          break;
        } catch {
          // Transient API/cold-start failure; keep polling the durable operation.
        }
      }
      if (!landed) {
        clearSwrCacheByPrefix('events:');
        toast.info('Recalculation is still running. Updated details will appear when it finishes.');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Could not recalculate: ${msg}`);
    } finally {
      release();
    }
  };

  const markWrongDate = async (event: ExtractedEvent, correction?: DateCorrection): Promise<void> => {
    if (markingWrongIds.has(event.rowKey)) return;
    setMarkingWrongIds((prev) => {
      const next = new Set(prev);
      next.add(event.rowKey);
      return next;
    });
    const release = () => setMarkingWrongIds((prev) => {
      if (!prev.has(event.rowKey)) return prev;
      const next = new Set(prev);
      next.delete(event.rowKey);
      return next;
    });
    try {
      const updated = await api.post<ExtractedEvent>(
        `/api/mgmt/events/${encodeURIComponent(event.rowKey)}/flag-wrong-date`,
        correction ?? {},
      );
      // Optimistically fold the authoritative date/flag fields onto the row so
      // the change shows instantly, independent of the public endpoint's
      // response cache. Only date/flag columns are copied so existing
      // venue/series/tag enrichment already on the row is preserved.
      onEvents((prev) => prev
        ? prev.map((ev) => ev.rowKey === event.rowKey
          ? {
              ...ev,
              startUtc: updated.startUtc,
              endUtc: updated.endUtc,
              isAllDay: updated.isAllDay,
              dateFlaggedWrong: updated.dateFlaggedWrong,
              dateFlaggedWrongAt: updated.dateFlaggedWrongAt,
              dateFlaggedWrongBy: updated.dateFlaggedWrongBy,
              partitionKey: updated.partitionKey,
              updatedAt: updated.updatedAt,
            }
          : ev)
        : prev);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Could not update event: ${msg}`);
      release();
      return;
    }
    toast.success(correction ? 'Date corrected.' : 'Marked as wrong date/time.');
    try {
      const fresh = await fetchEvents();
      if (eventsCacheKey) writeSwrCache(eventsCacheKey, fresh);
      onEvents(fresh);
    } catch {
      clearSwrCacheByPrefix('events:');
    }
    release();
  };

  return { recalculatingIds, markingWrongIds, recalculateDate, markWrongDate };
}
